import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useCategoryTreeItem } from '../categoryTree';
import { CategoryUidaAsset, UIDAPresentationTypeEnum, tvpil } from '../compat/tvpil';
import type { RhinoImageRatio } from '../image';
import { queryKeys } from '../queryKeys';
import { useImageSettings } from '../settings/useImageSettings';

import { fetchCollectionMeta } from './fetchCollectionMeta';

type UseCollectionMetaProps = {
  defaultImageRatio?: RhinoImageRatio;
  defaultLandscapeImageHeight?: number;
  defaultPortraitImageHeight?: number;
  gridReferenceId: string;
  imageSize?: 'compact' | 'default' | 'medium';
  railReferenceId: string;
};

/**
 * @see https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/2196572344/Grid#Types-of-GRID-widget
 */
export function useCollectionMeta({
  gridReferenceId,
  railReferenceId,
  imageSize = 'default',
  defaultPortraitImageHeight,
  defaultLandscapeImageHeight,
  defaultImageRatio,
}: UseCollectionMetaProps) {
  const railCategory = useCategoryTreeItem(railReferenceId);
  const gridCategory = useCategoryTreeItem(gridReferenceId);

  const imageSettings = useImageSettings({
    referenceId: gridReferenceId,
    imageSize,
    defaultPortraitImageHeight,
    defaultLandscapeImageHeight,
    defaultImageRatio,
  });

  const collectionMetaId = Number(railReferenceId);
  const isEnabled = !Number.isNaN(collectionMetaId) && Boolean(gridCategory) && !railCategory;
  const {
    isLoading: queryLoading,
    data: meta,
    error,
  } = useQuery({
    queryKey: queryKeys.collectionMeta(collectionMetaId),
    queryFn: () => fetchCollectionMeta(collectionMetaId),
    enabled: isEnabled,
  });
  const isLoading = railCategory ? false : queryLoading;

  const isGroupedGrid = Boolean(tvpil.dms.GridCharacteristics?.GRID_TYPES.includes(gridReferenceId));

  const settings = useMemo(() => {
    // gridCategory shouldn't be undefined, but it's necessary for type safety
    if (!gridCategory || !(meta || railCategory)) {
      return undefined;
    }

    return {
      ...imageSettings,
      branding: gridCategory.uiDesignElements,
      collectionId: railCategory ? railCategory.channels[0] : collectionMetaId,
      groupBy: getGroupBy(isGroupedGrid, railCategory),
      isFilterByEntitlementRequired: Boolean(meta?.isFilterByEntitlementRequired),
      isGrouped: isGroupedGrid,
      kSql: railCategory?.aggregatedKSQL,
      parentalPinProtected: gridCategory.parentalPinProtected,
      shouldShowEntitlementIndicator: gridCategory.asideProperty?.presentEntitlementIndicator,
      shouldShowRating: Boolean(gridCategory.asideProperty?.presentRating),
      providerName: gridCategory.name,
      title: railCategory?.name ?? meta?.name,
    };
  }, [gridCategory, imageSettings, railCategory, collectionMetaId, isGroupedGrid, meta]);

  return {
    data: settings,
    isLoading,
    error,
  };
}

function getGroupBy(isGroupedGrid: boolean, railCategory?: CategoryUidaAsset) {
  if (railCategory?.subtype === UIDAPresentationTypeEnum.series_catch_up) {
    return 'SeriesID';
  }

  if (railCategory?.groupBy) {
    return railCategory.groupBy;
  }

  if (isGroupedGrid) {
    return tvpil.dms.GridCharacteristics?.GROUP_BY_VALUE;
  }
}
